import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Facebook, MessageCircle, Star } from "lucide-react";
import Logo from "../components/Logo";
import { ButtonLink } from "../components/Buttons";
import { site, navLinks } from "../config/site";
import { serviceCategories } from "../data/services";

const SocialIcon = ({ label, href, children, testId }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center border border-white/15 text-ivory/80 transition-colors hover:border-champagne hover:text-champagne"
    >
      {children}
    </a>
  ) : (
    <span
      data-testid={testId}
      aria-disabled="true"
      title={`${label} — link coming soon`}
      className="flex h-11 w-11 cursor-not-allowed items-center justify-center border border-white/10 text-ivory/30"
    >
      {children}
    </span>
  );

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="grain-dark bg-ink text-ivory" data-testid="site-footer">
      <div className="container-x grid gap-14 py-16 md:py-20 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo dark />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-ivory/60">
            {site.footerDescription}
          </p>
          <div className="mt-8 flex gap-3">
            <SocialIcon label="Instagram (link coming soon)" href={site.social.instagram} testId="social-instagram">
              <Instagram className="h-4 w-4" />
            </SocialIcon>
            <SocialIcon label="Facebook (link coming soon)" href={site.social.facebook} testId="social-facebook">
              <Facebook className="h-4 w-4" />
            </SocialIcon>
            <SocialIcon label="Google Business Profile (link coming soon)" href={site.social.googleBusiness} testId="social-google">
              <Star className="h-4 w-4" />
            </SocialIcon>
            <SocialIcon label="WhatsApp" href={site.social.whatsapp} testId="social-whatsapp">
              <MessageCircle className="h-4 w-4" />
            </SocialIcon>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="eyebrow-light">Explore</h3>
          <ul className="mt-6 space-y-3">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-ivory/70 transition-colors hover:text-champagne"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="eyebrow-light">Services</h3>
          <ul className="mt-6 space-y-3">
            {serviceCategories.slice(0, 6).map((c) => (
              <li key={c}>
                <Link
                  to={`/services?category=${encodeURIComponent(c)}`}
                  className="text-sm text-ivory/70 transition-colors hover:text-champagne"
                >
                  {c}
                </Link>
              </li>
            ))}
          </ul>
          <ButtonLink to="/book" variant="gold" className="mt-8" data-testid="footer-book-btn">
            Book an Appointment
          </ButtonLink>
        </div>

        <div className="lg:col-span-3">
          <h3 className="eyebrow-light">Visit Us</h3>
          <ul className="mt-6 space-y-5 text-sm text-ivory/70">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-champagne" />
              <span>
                {site.addressLine1}
                <br />
                {site.addressLine2}
              </span>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-champagne" />
              <a href={site.phoneHref} className="transition-colors hover:text-champagne">
                {site.phoneDisplay}
              </a>
            </li>
            <li className="flex gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-champagne" />
              <span>
                {site.hoursDays}
                <br />
                {site.hoursTime}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 pb-24 text-xs text-ivory/45 md:flex-row lg:pb-6">
          <p>
            © {year} {site.fullName}. All rights reserved.
          </p>
          <p className="tracking-wide">Unisex Salon in Banjara Hills, Hyderabad</p>
        </div>
      </div>
    </footer>
  );
}
